import React, { ReactNode, useEffect, useState } from "react";
import { X, Plus, Settings, Eye, Download } from "lucide-react";
import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import CompanyLogo from "@/assets/Dashboard_Card.png";
import Icon1 from "@/assets/home/card/Icon1.svg";
import Icon2 from "@/assets/home/card/Icon2.svg";
import Icon3 from "@/assets/home/card/Icon3.svg";
import Icon4 from "@/assets/home/card/Icon4.svg";
import Icon5 from "@/assets/home/card/Icon5.svg";
import Icon6 from "@/assets/home/card/Icon6.svg";
import {
  useLocation,
  useSearchParams,
  useParams,
  useNavigate,
} from "react-router-dom";
import CampaignDashboardVisualView from "./components/campaign-detail-visual-view";
import useCampaignDetailStore from "./store/useCampaignDetailStore";
import useCampaignStore from "@/pages/campaign/store/campaignStore";
import Spinner from "@/components/ui/Spinner";
import CampaignDetailAdCategoryDetails from "./components/ad-category-details";
import useGlobalStore from "@/store/globalStore";
import AdBuilder from "./components/ad-builder/index";
import CampaignDetailListView from "./components/campaign-detail-list-view";
import RecentlyDeletedPanel from "./components/recently-deleted-panel";
import VersionUpdatesPanel from "./components/version-updates-panel";

interface TeamMember {
  initials: string;
  color: string;
}

interface StatusItem {
  label: string;
  value: number;
  cumulativeValue: number;
  /** Tailwind class for the filled bar */
  barColor: string;
  /** Tailwind class for the unfilled track */
  trackColor: string;
  iconSrc: string;
}

const CampaignDetailWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [isLoading, setIsLoading] = useState(false);

  const setError = useGlobalStore((state) => state.setError);

  const location = useLocation();

  const { campaignDetail, getCampaignDetailData } = useCampaignDetailStore();
  const downloadCampaignAssets = useCampaignStore(
    (state) => state.downloadCampaignAssets,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportAssets = async () => {
    if (!campaignId || isExporting) return;
    try {
      setIsExporting(true);
      await downloadCampaignAssets(campaignId);
    } catch (error: any) {
      setError(error.message ?? "Failed to export campaign assets");
    } finally {
      setIsExporting(false);
    }
  };
  const [activeView, setActiveView] = useState<"visual" | "list" | "blocking">(
    "visual",
  );
  const [activeFilter, setActiveFilter] = useState("All Flights");

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await getCampaignDetailData(campaignId);
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch campaign data on mount
  useEffect(() => {
    if (campaignId) {
      fetchData();
    }
  }, [campaignId]);

  // Set view from URL params
  useEffect(() => {
    const view = searchParams.get("view");
    if (view) {
      setActiveView(view as "visual" | "list" | "blocking");
    }
  }, []);

  // Generate team member initials and colors from actual team data
  const teamMembers: TeamMember[] =
    campaignDetail?.metadata.teams.map((team, idx) => ({
      initials: team.name
        .split(" ")
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase(),
      color: idx % 2 === 0 ? "bg-pink-500" : "bg-blue-500",
    })) || [];

  // Calculate total assets
  const totalAssets =
    campaignDetail?.nested_by_language_objective.reduce(
      (sum, lang) =>
        sum +
        lang.objectives.reduce(
          (objSum, obj) => objSum + obj.metrics.total_assets,
          0,
        ),
      0,
    ) || 0;

  const sc =
    activeFilter === "All Flights"
      ? campaignDetail?.metadata?.status_counts
      : campaignDetail?.nested_by_flight?.find(
          (f) => `Flight ${f.flight_number}` === activeFilter,
        )?.status_counts || campaignDetail?.metadata?.status_counts;

  const totalStatusAssets = sc?.total ?? totalAssets;

  const liveCount = sc?.live ?? 0;
  const traffickingCount = sc?.trafficking ?? 0;
  const approvedCount = sc?.approved ?? 0;
  const inReviewCount = sc?.in_review ?? 0;
  const inProgressCount = sc?.in_progress ?? 0;
  const briefedCount = sc?.briefed ?? 0;

  const cumLive = liveCount;
  const cumTrafficking = cumLive + traffickingCount;
  const cumApproved = cumTrafficking + approvedCount;
  const cumInReview = cumApproved + inReviewCount;
  const cumInProgress = cumInReview + inProgressCount;
  const cumBriefed = cumInProgress + briefedCount;

  // Status items wired to real API data with XD colors
  const statusItems: StatusItem[] = [
    {
      label: "Briefed",
      value: briefedCount,
      cumulativeValue: cumBriefed,
      barColor: "bg-yellow-600",
      trackColor: "bg-yellow-200",
      iconSrc: Icon2,
    },
    {
      label: "In Progress",
      value: inProgressCount,
      cumulativeValue: cumInProgress,
      barColor: "bg-orange-600",
      trackColor: "bg-orange-200",
      iconSrc: Icon3,
    },
    {
      label: "In Review",
      value: inReviewCount,
      cumulativeValue: cumInReview,
      barColor: "bg-red-600",
      trackColor: "bg-red-200",
      iconSrc: Icon4,
    },
    {
      label: "Approved",
      value: approvedCount,
      cumulativeValue: cumApproved,
      barColor: "bg-lime-500",
      trackColor: "bg-lime-200",
      iconSrc: Icon1,
    },
    {
      label: "Trafficking",
      value: traffickingCount,
      cumulativeValue: cumTrafficking,
      barColor: "bg-indigo-400",
      trackColor: "bg-indigo-200",
      iconSrc: Icon6,
    },
    {
      label: "Live",
      value: liveCount,
      cumulativeValue: cumLive,
      barColor: "bg-cyan-400",
      trackColor: "bg-cyan-200",
      iconSrc: Icon5,
    },
  ];

  // Extract unique ad types from campaign data
  const adTypes =
    campaignDetail?.nested_by_flight
      .flatMap((flight) =>
        flight.flight_rows.flatMap((row) =>
          row.flight_ad_categories.flatMap((cat) =>
            cat.ad_rows.map((ad) => ad.ad_type),
          ),
        ),
      )
      .filter((value, index, self) => self.indexOf(value) === index) || [];

  // Get languages
  const languages =
    campaignDetail?.metadata.languages
      .map((lang) => lang.name.slice(0, 2).toUpperCase())
      .join(", ") || "";

  // Format date range
  const formatDateRange = () => {
    if (
      !campaignDetail?.nested_by_flight?.length ||
      !campaignDetail.nested_by_flight[0]
    )
      return "N/A";
    const flights = campaignDetail.nested_by_flight;
    const startDate = new Date(flights[0]?.start_date).toLocaleDateString();
    const endDate = new Date(
      flights[flights.length - 1]?.end_date,
    ).toLocaleDateString();
    return `${startDate} – ${endDate}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (!campaignDetail) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">No campaign data found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Main Content */}
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Campaign Dashboard</h1>
          <div className="flex justify-between">
            <Button
              type="secondary"
              leadingIcon={<Download size={20} />}
              className="mr-2"
              onClick={handleExportAssets}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Export Assets"}
            </Button>
            <Button
              type="secondary"
              leadingIcon={<Settings size={20} />}
              className="mr-2"
              onClick={() => {
                navigate(`/campaign/flight?id=${campaignId}`);
              }}
            >
              Edit Flights
            </Button>
            <Button
              type="secondary"
              leadingIcon={<Settings size={20} />}
              className="mr-2"
              onClick={() => {
                navigate(`/campaign/assets?id=${campaignId}`);
              }}
            >
              Edit Assets
            </Button>

            <Button
              type="secondary"
              leadingIcon={<Settings size={20} />}
              onClick={() => {
                navigate(`/campaign?id=${campaignId}`);
              }}
            >
              Campaign Settings
            </Button>
          </div>
        </div>

        {/* Ad category changes this campaign has not answered yet. */}
        {campaignId && (
          <VersionUpdatesPanel
            campaignId={campaignId}
            openOnLoad={searchParams.get("versionUpdates") === "open"}
            onDialogClosed={() => {
              if (!searchParams.has("versionUpdates")) return;
              const next = new URLSearchParams(searchParams);
              next.delete("versionUpdates");
              setSearchParams(next, { replace: true });
            }}
          />
        )}

        {/* Campaign Card */}
        <div className="mb-8 mt-14">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div>
                <img
                  src={campaignDetail.metadata.header_image?.url || CompanyLogo}
                  className="rounded-lg w-20 h-20 object-cover"
                />
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {campaignDetail.metadata.teams?.filter(
                    (e) => e.team_type == "client",
                  )?.[0]?.name || "Agency"}
                </div>
                <h2 className="text-2xl font-medium">
                  {campaignDetail.metadata.campaign.name}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full overflow-hidden border border-[#EA33C0]">
                <button
                  onClick={() => {
                    navigate(`/campaign/${campaignId}?view=visual`);
                    setActiveView("visual");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors border-r border-[#EA33C0] ${
                    activeView === "visual"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Visual View
                </button>
                <button
                  onClick={() => {
                    navigate(`/campaign/${campaignId}?view=list`);
                    setActiveView("list");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors border-r border-[#EA33C0] ${
                    activeView === "list"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  List View
                </button>
                <button
                  onClick={() => {
                    navigate(`/campaign/${campaignId}?view=blocking`);
                    setActiveView("blocking");
                  }}
                  className={`px-8 py-2 font-medium text-sm transition-colors ${
                    activeView === "blocking"
                      ? "bg-[#EA33C0] text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Blocking Chart
                </button>
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-4 text-pink-500 p-2 rounded"
              >
                {isExpanded ? <X size={28} /> : <Plus size={28} />}
              </button>
            </div>
          </div>

          {isExpanded && (
            <>
              {/* Project Details */}
              <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
                <div>
                  <div className="grid grid-cols-4 gap-6 mb-6">
                    <div>
                      <div className="font-semibold mb-1">Project Number</div>
                      <div>
                        {campaignDetail.metadata.campaign.project_code != null
                          ? campaignDetail.metadata.campaign.project_code
                          : "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Total Budget</div>
                      <div>
                        {campaignDetail?.metadata?.campaign?.budget != null
                          ? `$${campaignDetail.metadata.campaign.budget.toLocaleString()}`
                          : "N/A"}
                      </div>
                    </div>
                    <div className="mb-6 col-span-2">
                      <div className="font-semibold mb-1">In Market Date</div>
                      <div>{formatDateRange()}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-6 mb-6">
                    <div>
                      <div className="font-semibold mb-1">Total Flights</div>
                      <div>{campaignDetail.nested_by_flight.length}</div>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Languages</div>
                      <div>{languages}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold mb-2">Team</div>
                      <div className="flex gap-2">
                        {teamMembers.map((member, idx) => (
                          <div
                            key={idx}
                            className={`w-8 h-8 rounded-full ${member.color} text-white flex items-center justify-center font-semibold text-xs`}
                          >
                            {member.initials}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-2">Ad Types</div>
                    <div className="flex flex-wrap gap-2">
                      {adTypes.map((type, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-1.5 bg-white border border-gray-300 rounded-full text-sm"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status Tracker */}
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 mr-4 min-w-0">
                      <div className="text-lg font-semibold mb-3">
                        Status Tracker
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hidden">
                        {[
                          "All Flights",
                          ...campaignDetail.nested_by_flight.map(
                            (flight, idx) => `Flight ${flight.flight_number}`,
                          ),
                        ].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                              activeFilter === filter
                                ? "bg-gray-700 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {filter}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-4xl font-bold">
                        {totalStatusAssets}
                      </div>
                      <div className="text-sm text-gray-600">Total Assets</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {statusItems.map((item, idx) => {
                      const pct =
                        totalStatusAssets > 0
                          ? Math.round(
                              (item.cumulativeValue / totalStatusAssets) * 100,
                            )
                          : 0;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-24 text-sm font-medium text-gray-700">
                            {item.label}
                          </div>
                          <div className="flex-1 relative">
                            <div
                              className={`h-8 rounded-full overflow-hidden relative ${item.trackColor}`}
                            >
                              {pct > 0 && (
                                <div
                                  className={`absolute left-0 top-0 h-full ${item.barColor} rounded-full transition-all duration-500`}
                                  style={{ width: `${Math.max(pct, 6)}%` }}
                                />
                              )}
                              <div className="absolute inset-0 flex items-center justify-between px-3">
                                <img
                                  src={item.iconSrc}
                                  alt={item.label}
                                  className="w-6 h-6 drop-shadow"
                                />
                                {item.cumulativeValue > 0 && (
                                  <span className="font-semibold text-sm text-gray-800">
                                    {item.cumulativeValue}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <hr className="mt-2 border-[#EA33C0] mb-8" />

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}` ? (
          <CampaignDashboardVisualView />
        ) : null}

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}/ad-category` ? (
          <CampaignDetailAdCategoryDetails />
        ) : null}

        {activeView === "visual" &&
        location.pathname === `/campaign/${campaignId}/ad-builder` ? (
          <AdBuilder />
        ) : null}

        {activeView === "list" &&
        location.pathname === `/campaign/${campaignId}` ? (
          <CampaignDetailListView />
        ) : null}

        {/* Recently deleted sits at the foot of the campaign dashboard root,
            below whichever view is active. It is campaign-scoped (not view- or
            flight-scoped), so it belongs to the page rather than to any one
            view; and it is a recovery affordance, so it should not push the
            flights and assets down the page. It renders nothing when there is
            nothing to restore. Kept off the /ad-category and /ad-builder
            sub-routes, which are drilled-in contexts. */}
        {campaignId && location.pathname === `/campaign/${campaignId}` ? (
          <RecentlyDeletedPanel campaignId={campaignId} />
        ) : null}
      </div>
    </Layout>
  );
};

export default CampaignDetailWrapper;
